import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, getPendingReferralCode } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoogleIcon = (props) => (
  <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" {...props}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 31.9 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.5 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.5 29 4.5 24 4.5c-7.4 0-13.7 4.1-17.1 10.2z"/>
    <path fill="#4CAF50" d="M24 43.5c5 0 9.5-1.9 12.9-5l-6-4.9c-1.7 1.3-4 2.1-6.9 2.1-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.1 39.5 15.9 43.5 24 43.5z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2.1 3.7-3.9 4.9.1 0 6 4.9 6 4.9 4.3-3.9 7-9.7 7-16.3 0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

const GitHubIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 .5C5.6.5.5 5.6.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.3.8-.6v-2.1c-3.2.7-3.9-1.6-3.9-1.6-.5-1.4-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.6-.3-5.3-1.3-5.3-5.8 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.2 1.2.9-.3 1.9-.4 2.9-.4s2 .1 2.9.4c2.2-1.5 3.2-1.2 3.2-1.2.6 1.7.2 2.9.1 3.2.7.8 1.2 1.8 1.2 3.1 0 4.5-2.7 5.5-5.3 5.8.4.4.8 1.1.8 2.3v3.4c0 .3.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.6 18.4.5 12 .5z"/>
  </svg>
);

const formatError = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ') || fallback;
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return fallback;
};

export default function SignInModal({ open, onOpenChange, initialMode = 'choose' }) {
  // mode: 'choose' | 'signup' | 'login' | 'magic' | 'forgot'
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { loginWithGoogle, loginWithGitHub, signupWithEmail, loginWithEmail, requestMagicLink, requestPasswordReset } = useAuth();

  const resetState = () => {
    setMode('choose');
    setEmail('');
    setPassword('');
    setName('');
    setSubmitting(false);
    setMagicSent(false);
    setResetSent(false);
  };

  const handleClose = (val) => {
    if (!val) resetState();
    onOpenChange(val);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signupWithEmail({ email, password, name, referral_code: getPendingReferralCode() });
      toast.success('Welcome to Transformerus! Check your inbox to verify your email.');
      handleClose(false);
      window.location.href = '/build';
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail, 'Could not sign you up'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginWithEmail({ email, password });
      toast.success('Signed in. Welcome back!');
      handleClose(false);
      window.location.href = '/build';
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail, 'Could not sign you in'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMagic = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestMagicLink({ email, referral_code: getPendingReferralCode() });
      setMagicSent(true);
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail, 'Could not send sign-in link'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset({ email });
      setResetSent(true);
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail, 'Could not send reset link'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="signin-modal"
        className="max-w-md p-0 overflow-hidden bg-white text-neutral-900 border border-neutral-200"
      >
        <div className="px-6 pt-6 pb-2">
          <DialogHeader className="space-y-1">
            {mode !== 'choose' && (
              <button
                onClick={() => setMode('choose')}
                data-testid="auth-back"
                className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-900 mb-2"
                type="button"
              >
                <ArrowLeft className="w-3 h-3" /> back
              </button>
            )}
            <DialogTitle className="font-display text-2xl tracking-tight">
              {mode === 'choose' && 'Sign in to Transformerus'}
              {mode === 'signup' && 'Create your account'}
              {mode === 'login' && 'Welcome back'}
              {mode === 'magic' && (magicSent ? 'Check your email' : 'Get a magic link')}
              {mode === 'forgot' && (resetSent ? 'Check your email' : 'Reset your password')}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-neutral-500">
              {mode === 'choose' && 'Pick how you want to continue. We never post anywhere on your behalf.'}
              {mode === 'signup' && 'Get 10 starter credits the second your account is ready.'}
              {mode === 'login' && 'Sign in with the email you registered with.'}
              {mode === 'magic' && !magicSent && "Enter your email and we'll send you a one-click sign-in link."}
              {mode === 'magic' && magicSent && `We sent a sign-in link to ${email}. It expires in 15 minutes.`}
              {mode === 'forgot' && !resetSent && "Type your email and we'll send a link to choose a new password."}
              {mode === 'forgot' && resetSent && `If an account exists for ${email}, we sent a reset link.`}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-6 pt-3 space-y-3">
          {mode === 'choose' && (
            <>
              <Button
                onClick={loginWithGoogle}
                data-testid="signin-google-btn"
                variant="outline"
                className="w-full h-11 rounded-xl border-neutral-300 hover:bg-neutral-50 flex items-center justify-center gap-2.5 text-[14px] font-medium"
              >
                <GoogleIcon /> Continue with Google
              </Button>
              <Button
                onClick={loginWithGitHub}
                data-testid="signin-github-btn"
                className="w-full h-11 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white flex items-center justify-center gap-2.5 text-[14px] font-medium"
              >
                <GitHubIcon /> Continue with GitHub
              </Button>

              <div className="flex items-center gap-3 py-1">
                <span className="h-px bg-neutral-200 flex-1" />
                <span className="text-[11px] uppercase tracking-wider text-neutral-400">or</span>
                <span className="h-px bg-neutral-200 flex-1" />
              </div>

              <Button
                onClick={() => setMode('magic')}
                data-testid="signin-magic-link-btn"
                variant="outline"
                className="w-full h-11 rounded-xl border-neutral-300 hover:bg-neutral-50 flex items-center justify-center gap-2.5 text-[14px] font-medium"
              >
                <Mail className="w-4 h-4" /> Email me a magic link
              </Button>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => setMode('login')}
                  data-testid="signin-email-login-btn"
                  variant="ghost"
                  className="h-10 rounded-xl text-[13px] text-neutral-700"
                >
                  Sign in with password
                </Button>
                <Button
                  onClick={() => setMode('signup')}
                  data-testid="signin-email-signup-btn"
                  variant="ghost"
                  className="h-10 rounded-xl text-[13px] text-neutral-700"
                >
                  Create account
                </Button>
              </div>
            </>
          )}

          {mode === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-2.5">
              <Input
                data-testid="signup-name"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-xl"
              />
              <Input
                data-testid="signup-email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 rounded-xl"
              />
              <Input
                data-testid="signup-password"
                type="password"
                placeholder="Password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="h-11 rounded-xl"
              />
              <Button
                type="submit"
                data-testid="signup-submit"
                disabled={submitting}
                className="w-full h-11 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-medium"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create account'}
              </Button>
              <p className="text-[11px] text-neutral-500 text-center pt-1">
                By signing up you agree to our{' '}
                <a href="/terms" className="underline">Terms</a> and{' '}
                <a href="/privacy" className="underline">Privacy Policy</a>.
              </p>
            </form>
          )}

          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-2.5">
              <Input
                data-testid="login-email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 rounded-xl"
              />
              <Input
                data-testid="login-password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 rounded-xl"
              />
              <Button
                type="submit"
                data-testid="login-submit"
                disabled={submitting}
                className="w-full h-11 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-medium"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
              </Button>
              <button
                type="button"
                onClick={() => setMode('forgot')}
                data-testid="login-forgot-link"
                className="block text-[12px] text-neutral-500 hover:text-neutral-900 underline mt-1 mx-auto"
              >
                Forgot your password?
              </button>
            </form>
          )}

          {mode === 'magic' && !magicSent && (
            <form onSubmit={handleMagic} className="space-y-2.5">
              <Input
                data-testid="magic-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 rounded-xl"
              />
              <Button
                type="submit"
                data-testid="magic-submit"
                disabled={submitting}
                className="w-full h-11 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-medium"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send sign-in link'}
              </Button>
            </form>
          )}

          {mode === 'forgot' && !resetSent && (
            <form onSubmit={handleForgot} className="space-y-2.5">
              <Input
                data-testid="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 rounded-xl"
              />
              <Button
                type="submit"
                data-testid="forgot-submit"
                disabled={submitting}
                className="w-full h-11 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-medium"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
              </Button>
            </form>
          )}

          {(magicSent || resetSent) && (
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                <Mail className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-[13px] text-neutral-500">
                Didn't get it? Check spam, or{' '}
                <button
                  type="button"
                  onClick={() => { setMagicSent(false); setResetSent(false); }}
                  className="underline text-neutral-700"
                >
                  try again
                </button>
                .
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
